export const PLATFORM_NAME = 'AuctiSync' as const;
export const TAGLINE = "The auction platform built for what's next." as const;

/** Market segments AuctiSync serves. See business/strategy/WHITESPACE_STRATEGY.md for details. */
export const SEGMENTS = {
  LIEN_LAW: 'lien-law',
  LIQUIDATION: 'liquidation',
  LIVE_COMMERCE: 'live-commerce',
} as const;

export type Segment = (typeof SEGMENTS)[keyof typeof SEGMENTS];

/**
 * Pricing tiers. See business/strategy/PRICING.md for full tier feature matrix.
 *
 * ⚠️ This list MUST stay in step with the `tenants_tier_check` constraint in the
 * database. It did not: the constraint has always permitted `complimentary` and
 * `trial`, neither of which existed here, so `hasFeature` — which denies unknown
 * tiers by default — refused EVERY gated feature for any tenant on one. DTA, the
 * first customer, sits on `complimentary` and was silently entitled to nothing,
 * white-label removal included. Found 2026-08-13 while asking why the app showed
 * AuctiSync branding everywhere.
 */
export const TIERS = {
  /**
   * Comped account — full access, no charge. Jim, 2026-08-13: "the complimentary
   * tier gets everything." Ranked above Enterprise Plus so it clears every gate
   * including ones added later, which is the point of a comp.
   */
  COMPLIMENTARY: 'complimentary',
  /**
   * Time-limited full access. Jim, 2026-08-25: "For the demo, why not show all
   * available features? We would make a note that it is Professional tier
   * option." So a trial sees everything and each gated surface names the tier
   * that actually carries it — `featureTierLabel()` in ../features supplies
   * that label. Ranked above Enterprise Plus for the same reason
   * `complimentary` is: a trial that silently stops covering a feature added
   * next month would misrepresent the product to the exact person evaluating
   * it.
   */
  TRIAL: 'trial',
  SOLO: 'solo',
  FUNDRAISER: 'fundraiser',
  STARTER: 'starter',
  TCG_STARTER: 'tcg_starter',
  STORAGE: 'storage',
  GROWTH: 'growth',
  PRO: 'pro',
  ELITE: 'elite',
  ENTERPRISE: 'enterprise',
  ENTERPRISE_PLUS: 'enterprise_plus',
} as const;

export type Tier = (typeof TIERS)[keyof typeof TIERS];

/** Phase sequencing per BUILD_PLAN.md. */
export const PHASES = {
  PHASE_1: 'phase_1',
  PHASE_1_5: 'phase_1_5',
  PHASE_2: 'phase_2',
  PHASE_3: 'phase_3',
} as const;

export type Phase = (typeof PHASES)[keyof typeof PHASES];
